import { useState, useEffect, useRef } from "react";
import { fmtMoneyExact, Money } from "@/lib/money";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { T, F, th as palette, I, Over, Pill, Btn } from "@/lib/bricks";
import AddressAutocomplete, { mapProvince } from "@/components/AddressAutocomplete";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import BricksLoader from "@/components/BricksLoader";
import InfoTip from "@/components/ui/info-tip";
import { Page, PageHeader, Meta, Stats, StatusTabs, Controls, Toolbar, Search, figLabel, List, Row, Empty, Section, TextLink, Loading, humanize } from "@/components/calm/Calm";
import {
  BOARD_LABEL, STATUS_LABEL, STATUS_TONE, defaultBoard, changedOverrides, draftReadiness,
  readinessLine, markReadyBlock, focusItem,
} from "@/lib/mlsSubmission";

const MLS_STATUS_TONE = { active:'ink', sold:'neutral', expired:'brick', withdrawn:'neutral' };

const fmtCAD = fmtMoneyExact;
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-CA', { year:'numeric', month:'short', day:'numeric' });
}

const EMPTY_FORM = {
  mls_number:'', property_address:'', city:'', province:'ON', postal_code:'',
  list_price:'', bedrooms:'', bathrooms:'', sqft:'', property_type:'Detached', list_date:'', status:'active',
  // Location & Legal
  legal_description:'', lot_front:'', lot_depth:'', lot_size_code:'Feet', zoning:'',
  // Amounts & Dates
  taxes:'', tax_year:'', possession_date:'', possession_remarks:'',
  // Exterior & Structure
  style:'', exterior_finish:'', approx_age:'', garage_type:'', garage_spaces:'',
  drive_parking_spaces:'', total_parking_spaces:'', pool:'', waterfront:'None',
  // Interior
  kitchens:'', rooms_total:'', basement_type:'', heat_type:'', air_conditioning:'',
  // Remarks
  remarks_client:'', remarks_brokerage:'',
  // Listing Office/Agent
  listing_brokerage:'', listing_agent_name:'', listing_agent_phone:'', co_op_commission:'',
};
const BOARD_OPTIONS = [
  { value: 'trreb',    label: 'TRREB (Ontario)',        hint: 'via Matrix / PropTx' },
  { value: 'pillar9',  label: 'Pillar 9 (Alberta)',     hint: 'via Matrix — CREB / RAE' },
  { value: 'ddf',      label: 'CREA DDF (national)',    hint: 'Realtor.ca — opt-in only' },
];

// ─── Import form section helpers ────────────────────────────────────

function ImportSection({ title, p, children }) {
  return (
    <div style={{ borderTop: `1px solid ${p.hair}`, paddingTop: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: p.ink3, marginBottom: 12 }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>{children}</div>
    </div>
  );
}

function ImportField({ label, value, onChange, p, type = 'text', options, placeholder, span, hint }) {
  const inputStyle = { marginTop: 8, width: '100%', padding: '9px 11px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' };
  return (
    <div style={span ? { gridColumn: 'span 2' } : undefined}>
      <Over color={p.ink3}>{label}{hint && <span style={{ fontWeight: 400, opacity: 0.7 }}> ({hint})</span>}</Over>
      {options ? (
        <select value={value} onChange={onChange} style={{ ...inputStyle, background: p.bg }}>
          {options.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea value={value} onChange={onChange} rows={2} placeholder={placeholder} style={{ ...inputStyle, resize: 'vertical' }}/>
      ) : (
        <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={inputStyle}/>
      )}
    </div>
  );
}

// ─── Package dialog ─────────────────────────────────────────────────

// Advisory media/listing checks from the server (Photos ≥ 6, Room dimensions,
// Commission to co-op). Counts come from this listing's Bricks shoots; never
// block "Mark ready" (photos can reach the board from outside Bricks).
export function MediaChecks({ checks, p }) {
  if (!checks || !Array.isArray(checks.checks) || checks.checks.length === 0) return null;
  return (
    <div data-testid="mls-media-checks" style={{ display: 'flex', flexDirection: 'column' }}>
      {checks.checks.map(c => (
        <div key={c.key} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: `1px solid ${p.hair}` }}>
          <div style={{ width: 18, height: 18, borderRadius: 999, flexShrink: 0, background: c.ok ? T.okTint : T.warnTint, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
            <I name={c.ok ? 'chk' : 'x'} size={11} color={c.ok ? T.ok : T.warn} sw={2.4}/>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: c.ok ? p.ink : 'var(--c-warn)' }}>{c.label}</div>
            <div style={{ fontSize: 11.5, color: p.ink3, marginTop: 2 }}>{c.detail}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function PackageDialog({ listing, dark, p, session, BASE, onClose, initialBoard, onChanged }) {
  const [board, setBoard] = useState(() => initialBoard || defaultBoard(listing.province));
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(null);
  const [liveOpen, setLiveOpen] = useState(false);
  const [liveMls, setLiveMls] = useState('');

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };

  const loadPkg = (quiet = false) => {
    if (!quiet) { setLoading(true); setPkg(null); }
    return fetch(`${BASE}/api/mls/listings/${listing.id}/package?board=${board}`, { headers })
      .then(r => r.json())
      .then(d => { setPkg(d); setOverrides({}); setLoading(false); })
      .catch(() => { toast.error("Failed to load package"); setLoading(false); });
  };

  useEffect(() => {
    setOverrides({});
    setLiveOpen(false);
    loadPkg();
  }, [board, listing.id]); // eslint-disable-line

  const changes = pkg ? changedOverrides(overrides, pkg.sections) : {};
  const unsaved = Object.keys(changes).length;
  const readiness = pkg?.sections ? draftReadiness(pkg.sections, overrides) : null;
  const sub = pkg?.submission || { status: 'draft' };
  const status = sub.status || 'draft';
  const readyBlock = markReadyBlock(readiness, unsaved);
  const boardName = BOARD_LABEL[board] || board;
  const valueOf = (key) => {
    if (Object.prototype.hasOwnProperty.call(overrides, key)) return overrides[key];
    const f = (pkg?.sections || []).flatMap(s => s.fields).find(x => x.key === key);
    return f ? f.value : listing[key];
  };

  async function handleSave() {
    if (!unsaved) return;
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/mls/listings/${listing.id}`, { method: 'PATCH', headers, body: JSON.stringify(changes) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const bad = body?.detail?.fields ? Object.entries(body.detail.fields).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(' · ') : null;
        toast.error(bad || (typeof body.detail === 'string' ? body.detail : 'Could not save to the listing'));
        return;
      }
      const n = (body.saved || []).length;
      toast.success(`Saved ${n} field${n === 1 ? '' : 's'} to the listing`);
      await loadPkg(true);
      onChanged && onChanged();
    } catch { toast.error('Could not save to the listing'); }
    finally { setSaving(false); }
  }

  async function setStatus(next, mlsNumber) {
    setStatusBusy(next);
    try {
      const res = await fetch(`${BASE}/api/mls/listings/${listing.id}/submission`, {
        method: 'POST', headers, body: JSON.stringify({ board, status: next, mls_number: mlsNumber || null }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = body?.detail;
        toast.error((d && (d.message || (typeof d === 'string' ? d : null))) || 'Could not update the submission');
        return;
      }
      toast.success(next === 'draft' ? 'Back to draft' : `Marked ${STATUS_LABEL[next].toLowerCase()} for ${boardName}`);
      setLiveOpen(false);
      await loadPkg(true);
      onChanged && onChanged();
    } catch { toast.error('Could not update the submission'); }
    finally { setStatusBusy(null); }
  }

  const stamp = (iso) => iso ? new Date(iso).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;
  const actBtn = { padding: '9px 14px', fontSize: 13 };
  const offBtn = { opacity: 0.45, cursor: 'not-allowed' };

  function handlePrint() {
    const win = window.open('', '_blank');
    if (!win || !pkg) return;
    const allFields = (pkg.sections || []).flatMap(s =>
      s.fields.map(f => ({ section: s.title, ...f, value: overrides[f.key] ?? f.value }))
    );
    const rows = allFields.map(f => `
      <tr style="${f.required && !f.value ? 'background:#fff3cd' : ''}">
        <td style="padding:6px 10px;border:1px solid #ddd;font-size:11px;color:#666;white-space:nowrap">${f.section}</td>
        <td style="padding:6px 10px;border:1px solid #ddd;font-size:12px;font-weight:500">${f.label}${f.required ? ' *' : ''}</td>
        <td style="padding:6px 10px;border:1px solid #ddd;font-size:12px;font-family:monospace">${f.value || '—'}</td>
        ${f.hint ? `<td style="padding:6px 10px;border:1px solid #ddd;font-size:11px;color:#888">${f.hint}</td>` : '<td style="border:1px solid #ddd"></td>'}
      </tr>`).join('');
    const notes = (pkg.notes || []).map(n => `<li style="margin:4px 0;font-size:12px">${n}</li>`).join('');
    win.document.write(`<html><head><title>MLS package · ${listing.mls_number || listing.property_address}</title>
      <style>body{font-family:system-ui,sans-serif;padding:24px;color:#111}h1{font-size:18px;margin-bottom:4px}h2{font-size:13px;color:#666;font-weight:400;margin-bottom:20px}table{border-collapse:collapse;width:100%}@media print{body{padding:12px}}</style>
      </head><body>
      <h1>${pkg.board} — MLS submission package</h1>
      <h2>${listing.property_address || ''} ${listing.city ? '· ' + listing.city : ''} ${listing.province ? '· ' + listing.province : ''}${readiness ? ' &nbsp;·&nbsp; ' + readinessLine(board, readiness) : ''}</h2>
      <p style="font-size:12px;background:#f5f5f4;padding:10px 14px;border-radius:4px;margin-bottom:20px">
        Submit via: <strong>${pkg.system}</strong> &nbsp;·&nbsp; <a href="${pkg.submission_url}">${pkg.submission_url}</a>
        ${pkg.coverage ? '&nbsp;·&nbsp; ' + pkg.coverage : ''}
      </p>
      <table><thead><tr style="background:#f5f5f4">
        <th style="padding:8px 10px;border:1px solid #ddd;font-size:11px;text-align:left">Section</th>
        <th style="padding:8px 10px;border:1px solid #ddd;font-size:11px;text-align:left">Field</th>
        <th style="padding:8px 10px;border:1px solid #ddd;font-size:11px;text-align:left">Value</th>
        <th style="padding:8px 10px;border:1px solid #ddd;font-size:11px;text-align:left">Notes</th>
      </tr></thead><tbody>${rows}</tbody></table>
      ${notes ? `<h3 style="margin-top:24px;font-size:13px">Submission notes</h3><ul>${notes}</ul>` : ''}
      <p style="font-size:10px;color:#aaa;margin-top:24px">Generated by Bricks · ${new Date().toLocaleString('en-CA')}</p>
      </body></html>`);
    win.document.close();
    win.print();
  }

  const inputStyle = (val, required) => ({
    width: '100%', padding: '7px 10px', fontSize: 12, fontFamily: F.mono,
    border: `1px solid ${required && !val ? '#f59e0b' : p.hair}`,
    borderRadius: 2, background: p.surf, color: p.ink, outline: 'none', boxSizing: 'border-box',
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      paddingTop: 48, overflowY: 'auto',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: p.surf, border: `1px solid ${p.hair}`, borderRadius: 2,
        width: 720, maxWidth: 'calc(100vw - 32px)', marginBottom: 48,
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '24px 28px 20px', borderBottom: `1px solid ${p.hair}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 500, fontFamily: F.display, color: p.ink }}>MLS submission package</div>
            <div style={{ fontSize: 12, color: p.ink3, marginTop: 4, fontFamily: F.display }}>
              {listing.property_address}{listing.city ? ` · ${listing.city}` : ''}{listing.province ? `, ${listing.province}` : ''}
            </div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', color: p.ink3, padding: 4 }}><I name="x" size={16} color={p.ink3}/></div>
        </div>

        {/* Board selector */}
        <div style={{ padding: '16px 28px', borderBottom: `1px solid ${p.hair}`, display: 'flex', gap: 8 }}>
          {BOARD_OPTIONS.map(opt => (
            <button key={opt.value} onClick={() => setBoard(opt.value)} style={{
              flex: 1, padding: '8px 10px', fontFamily: F.display, fontSize: 12, borderRadius: 2, cursor: 'pointer',
              border: `1px solid ${board === opt.value ? T.brick : p.hair}`,
              background: board === opt.value ? T.brickSoft : 'transparent',
              color: board === opt.value ? 'var(--c-attn)' : p.ink2, textAlign: 'left',
            }}>
              <div style={{ fontWeight: board === opt.value ? 600 : 400 }}>{opt.label}</div>
              <div style={{ fontSize: 10, opacity: 0.7, marginTop: 1 }}>{opt.hint}</div>
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '0 28px', overflowY: 'auto', maxHeight: 'calc(90vh - 240px)' }}>
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 12 }}>
              <BricksLoader variant="cycle" size={20} color={T.brick} />
              <span style={{ fontSize: 13, color: p.ink3 }}>Loading {BOARD_OPTIONS.find(b => b.value === board)?.label} fields…</span>
            </div>
          )}

          {!loading && pkg && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {/* Board info bar */}
              <div style={{ padding: '12px 0', marginBottom: 4, display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: p.ink3 }}>
                  <strong style={{ color: p.ink }}>Submit via:</strong> {pkg.system}
                </span>
                <a href={pkg.submission_url} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 12, color: T.brick, textDecoration: 'none' }}>
                  {pkg.submission_url} ↗
                </a>
                {pkg.coverage && (
                  <span style={{ fontSize: 11, color: p.ink4 }}>{pkg.coverage}</span>
                )}
              </div>

              {/* Validation + submission record */}
              {readiness && (
                <div data-testid="mls-validation" style={{ border: `1px solid ${p.hair}`, borderRadius: 2, padding: '18px 20px', marginBottom: 20, background: p.bg }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <Over color={p.ink3}>Validation</Over>
                      <div style={{ fontFamily: F.mono, fontSize: 12, color: readiness.ready ? p.ink2 : T.warn, marginTop: 8 }}>
                        {readinessLine(board, readiness)}{unsaved > 0 ? ' · unsaved' : ''}
                      </div>
                    </div>
                    <Pill tone={STATUS_TONE[status]} dark={dark}>{STATUS_LABEL[status]}</Pill>
                  </div>
                  <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column' }}>
                    {readiness.missing.length === 0 ? (
                      <div style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: `1px solid ${p.hair}` }}>
                        <div style={{ width: 18, height: 18, borderRadius: 999, flexShrink: 0, background: T.okTint, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                          <I name="chk" size={11} color={T.ok} sw={2.4}/>
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: p.ink }}>All {readiness.required_total} required fields ready</div>
                          <div style={{ fontSize: 11.5, color: p.ink3, marginTop: 2 }}>From this listing’s data</div>
                        </div>
                      </div>
                    ) : readiness.missing.map(m => (
                      <div key={m.key} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: `1px solid ${p.hair}` }}>
                        <div style={{ width: 18, height: 18, borderRadius: 999, flexShrink: 0, background: T.warnTint, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                          <I name="x" size={11} color={T.warn} sw={2.4}/>
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: T.warn }}>{m.label}</div>
                          <div style={{ fontSize: 11.5, color: p.ink3, marginTop: 2 }}>{m.section} · missing</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <MediaChecks checks={pkg.checks} p={p} />
                  {(sub.ready_at || sub.submitted_at || sub.live_at) && (
                    <div style={{ fontFamily: F.mono, fontSize: 11, color: p.ink4, marginTop: 12 }}>
                      {[sub.ready_at && `Validated ${stamp(sub.ready_at)}`, sub.submitted_at && `Submitted ${stamp(sub.submitted_at)}`, sub.live_at && `Live ${stamp(sub.live_at)}${sub.mls_number ? ` · MLS# ${sub.mls_number}` : ''}`].filter(Boolean).join('  ·  ')}
                    </div>
                  )}
                  {sub.recorded_board && (
                    <div style={{ fontSize: 11.5, color: p.ink3, marginTop: 10 }}>
                      Recorded on {BOARD_LABEL[sub.recorded_board] || sub.recorded_board} as {STATUS_LABEL[sub.recorded_status] || sub.recorded_status}.
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Btn variant={status === 'draft' ? 'accent' : 'ghost'} dark={dark} style={{ ...actBtn, ...(readyBlock || statusBusy ? offBtn : {}) }}
                      disabled={!!readyBlock || !!statusBusy} title={readyBlock || `Mark the ${boardName} package ready to submit`}
                      onClick={() => setStatus('ready')}>
                      {statusBusy === 'ready' ? 'Saving…' : 'Mark ready'}
                    </Btn>
                    <Btn variant={status === 'ready' ? 'accent' : 'ghost'} dark={dark} style={{ ...actBtn, ...(statusBusy ? offBtn : {}) }}
                      disabled={!!statusBusy} title={`Record that you submitted it in ${pkg.system}`}
                      onClick={() => setStatus('submitted')}>
                      {statusBusy === 'submitted' ? 'Saving…' : 'Mark submitted'}
                    </Btn>
                    <Btn variant={status === 'submitted' ? 'accent' : 'ghost'} dark={dark} style={{ ...actBtn, ...(statusBusy ? offBtn : {}) }}
                      disabled={!!statusBusy} title="Record the board's MLS number once it's active"
                      onClick={() => { setLiveMls(sub.mls_number || valueOf('mls_number') || ''); setLiveOpen(v => !v); }}>
                      Mark live
                    </Btn>
                    {status !== 'draft' && (
                      <button onClick={() => setStatus('draft')} disabled={!!statusBusy}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: p.ink3, fontFamily: F.display, padding: '4px 6px' }}>
                        Back to draft
                      </button>
                    )}
                  </div>
                  {readyBlock && status === 'draft' && (
                    <div style={{ fontSize: 11.5, color: p.ink3, marginTop: 8 }}>Mark ready unlocks when: {readyBlock.charAt(0).toLowerCase() + readyBlock.slice(1)}.</div>
                  )}
                  {liveOpen && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 200px' }}>
                        <Over color={p.ink3}>MLS number from {boardName}</Over>
                        <input autoFocus value={liveMls} onChange={e => setLiveMls(e.target.value)} placeholder="e.g. C7841902"
                          style={{ marginTop: 8, width: '100%', padding: '9px 11px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.mono, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
                      </div>
                      <Btn variant="accent" dark={dark} style={{ ...actBtn, ...(!liveMls.trim() || statusBusy ? offBtn : {}) }}
                        disabled={!liveMls.trim() || !!statusBusy} onClick={() => setStatus('live', liveMls.trim())}>
                        {statusBusy === 'live' ? 'Saving…' : 'Confirm live'}
                      </Btn>
                    </div>
                  )}
                  <div style={{ fontSize: 11.5, color: p.ink4, marginTop: 14, lineHeight: 1.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                    Submit in {pkg.system}, then mark it here.
                    <InfoTip label="How submission works">Bricks doesn't push to {boardName}. Submit in {pkg.system}, then record Submitted and Live here.{sub.recorded_board ? ` Marking a status here starts a ${boardName} record.` : ''}</InfoTip>
                  </div>
                </div>
              )}

              {/* Sections */}
              {pkg.sections?.map(section => (
                <div key={section.title} style={{ borderTop: `1px solid ${p.hair}`, paddingTop: 20, paddingBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: p.ink3, marginBottom: 14 }}>
                    {section.title}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px 20px' }}>
                    {section.fields.map(field => {
                      const val = overrides[field.key] ?? (field.value ?? '');
                      return (
                        <div key={field.key}>
                          <div style={{ fontSize: 11, color: p.ink3, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                            {field.label}
                            {field.required && <span style={{ color: '#f59e0b', fontWeight: 700 }}>*</span>}
                            {field.hint && (
                              <span style={{ fontSize: 10, color: p.ink4, fontStyle: 'italic' }}>— {field.hint}</span>
                            )}
                          </div>
                          {field.options ? (
                            <select
                              value={val}
                              onChange={e => setOverrides(prev => ({ ...prev, [field.key]: e.target.value }))}
                              style={{ ...inputStyle(val, field.required), fontFamily: F.display }}
                            >
                              <option value="">Select…</option>
                              {field.options.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={val}
                              onChange={e => setOverrides(prev => ({ ...prev, [field.key]: e.target.value }))}
                              style={inputStyle(val, field.required)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Notes */}
              {pkg.notes?.length > 0 && (
                <div style={{ borderTop: `1px solid ${p.hair}`, paddingTop: 20, paddingBottom: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: p.ink3, marginBottom: 12 }}>
                    Submission notes
                  </div>
                  <ul style={{ margin: 0, padding: '0 0 0 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {pkg.notes.map((note, i) => (
                      <li key={i} style={{ fontSize: 12, color: p.ink2 }}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 28px', borderTop: `1px solid ${p.hair}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: unsaved ? T.warn : p.ink4 }}>
            {pkg ? (unsaved ? `${unsaved} unsaved change${unsaved === 1 ? '' : 's'}` : `${readiness ? readiness.missing.length : 0} required fields still empty`) : ''}
          </span>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Btn variant="ghost" dark={dark} onClick={onClose}>Close</Btn>
            <Btn variant="secondary" dark={dark} onClick={handleSave} disabled={!unsaved || saving}
              style={!unsaved || saving ? offBtn : undefined}>
              {saving ? 'Saving…' : 'Save to listing'}
            </Btn>
            <Btn variant="primary" dark={dark} icon="print" onClick={handlePrint} disabled={!pkg}>
              Print / Save PDF
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── AI listing copy dialog ─────────────────────────────────────────

const TONE_OPTIONS = ['professional', 'luxury', 'casual', 'family-friendly'];

export function CopyGenDialog({ listing, dark, p, session, BASE, onClose, onSaved }) {
  const [tone, setTone] = useState('professional');
  const [generating, setGenerating] = useState(false);
  const [copy, setCopy] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await fetch(`${BASE}/api/mls/listings/${listing.id}/generate-copy`, { method: 'POST', headers, body: JSON.stringify({ tone }) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Generation failed');
      }
      const data = await res.json();
      setCopy(data);
    } catch (e) { setError(e.message || 'Failed to generate copy'); }
    finally { setGenerating(false); }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleSaveAsRemarks = async () => {
    if (!copy?.mls_description) return;
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/mls/listings`, {
        method: 'POST', headers,
        body: JSON.stringify({ ...listing, remarks_client: copy.mls_description }),
      });
      if (!res.ok) throw new Error();
      toast.success("Saved to listing remarks");
      onSaved();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const boxStyle = { padding: '12px 14px', border: `1px solid ${p.hair}`, borderRadius: 2, fontSize: 13, color: p.ink2, lineHeight: 1.5, whiteSpace: 'pre-wrap' };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent style={{ maxWidth: 560, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)', maxHeight: '90vh', overflowY: 'auto' }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Generate Listing Copy</DialogTitle>
          <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
            {listing.property_address} · from the fields on file.
          </DialogDescription>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <Over color={p.ink3}>Tone</Over>
              <select value={tone} onChange={e => setTone(e.target.value)}
                style={{ marginTop: 8, width: '100%', padding: '9px 11px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                {TONE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <Btn variant="primary" dark={dark} onClick={handleGenerate} disabled={generating}>{generating ? 'Generating…' : copy ? 'Regenerate' : 'Generate'}</Btn>
          </div>

          {error && <div style={{ fontSize: 12, color: T.brick }}>{error}</div>}

          {copy && (
            <>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <Over color={p.ink3}>Headline</Over>
                  <button onClick={() => handleCopyToClipboard(copy.headline)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: T.brick }}>Copy</button>
                </div>
                <div style={boxStyle}>{copy.headline}</div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <Over color={p.ink3}>MLS description</Over>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={() => handleCopyToClipboard(copy.mls_description)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: T.brick }}>Copy</button>
                    <button onClick={handleSaveAsRemarks} disabled={saving} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: T.brick }}>{saving ? 'Saving…' : 'Save to Listing'}</button>
                  </div>
                </div>
                <div style={boxStyle}>{copy.mls_description}</div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <Over color={p.ink3}>Social caption</Over>
                  <button onClick={() => handleCopyToClipboard(copy.social_caption)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: T.brick }}>Copy</button>
                </div>
                <div style={boxStyle}>{copy.social_caption}</div>
              </div>
            </>
          )}
        </div>
        <DialogFooter style={{ marginTop: 24, gap: 10 }}>
          <Btn variant="ghost" dark={dark} onClick={onClose}>Close</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── CMA dialog ─────────────────────────────────────────────────────

function CmaDialog({ listing, dark, p, session, BASE, onClose }) {
  const [loading, setLoading] = useState(false);
  const [cma, setCma] = useState(null);
  const [error, setError] = useState(null);
  const [excluded, setExcluded] = useState([]);
  const [analysis, setAnalysis] = useState('');
  const [analysisEdited, setAnalysisEdited] = useState(false);
  const [preparedFor, setPreparedFor] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(null); // 'pdf' | 'file'
  const [filed, setFiled] = useState(null);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };

  const handleGenerate = async (exclude = excluded) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE}/api/mls/listings/${listing.id}/cma`, {
        method: 'POST', headers, body: JSON.stringify({ exclude_comp_ids: exclude }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'CMA generation failed');
      }
      const data = await res.json();
      setCma(data);
      setAnalysis(data.analysis || '');
      setAnalysisEdited(false);
    } catch (e) { setError(e.message || 'Failed to generate CMA'); }
    finally { setLoading(false); }
  };

  useEffect(() => { handleGenerate([]); }, []); // eslint-disable-line

  const toggleComp = (key) => {
    const next = excluded.includes(key) ? excluded.filter(k => k !== key) : [...excluded, key];
    setExcluded(next);
    handleGenerate(next);
  };

  const reportBody = () => JSON.stringify({
    exclude_comp_ids: excluded,
    prepared_for: preparedFor,
    note,
    analysis: analysisEdited ? analysis : null,
  });

  const downloadPdf = async () => {
    setBusy('pdf');
    try {
      const res = await fetch(`${BASE}/api/mls/listings/${listing.id}/cma/report.pdf`, { method: 'POST', headers, body: reportBody() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Couldn't build the PDF");
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const slug = (listing.property_address || 'listing').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'listing';
      a.download = m ? m[1] : `cma-${slug}-${new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    } catch (e) { toast.error(e.message || "Couldn't build the PDF"); }
    finally { setBusy(null); }
  };

  const fileToDeal = async () => {
    setBusy('file');
    try {
      const res = await fetch(`${BASE}/api/mls/listings/${listing.id}/cma/report/file`, { method: 'POST', headers, body: reportBody() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Couldn't file the CMA");
      setFiled(data);
      toast.success("CMA saved to the deal's documents");
    } catch (e) { toast.error(e.message || "Couldn't file the CMA"); }
    finally { setBusy(null); }
  };

  const boxStyle = { padding: '12px 14px', border: `1px solid ${p.hair}`, borderRadius: 2, fontSize: 13, color: p.ink2, lineHeight: 1.5 };
  const inputStyle = { marginTop: 6, width: '100%', padding: '9px 11px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' };
  const linkBtn = { background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: T.brick, padding: 0, fontFamily: F.display, flexShrink: 0 };
  const compMeta = (c) => [c.source === 'mls' ? `Board MLS${c.mls_number ? ` ${c.mls_number}` : ''}` : 'Office deal',
    c.bedrooms ? `${c.bedrooms} bd` : null,
    c.bathrooms ? `${c.bathrooms} ba` : null,
    c.sqft ? `${c.sqft} sqft` : null,
    c.closing_date ? `sold ${c.closing_date}` : null].filter(Boolean).join(' · ');
  const linkedDeal = cma?.linked_transaction_id || listing.transaction_id;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent style={{ maxWidth: 560, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)', maxHeight: '90vh', overflowY: 'auto' }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Comparative Market Analysis</DialogTitle>
          <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
            {listing.property_address} · priced from sold comparables.
          </DialogDescription>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
          {loading && <div style={{ fontSize: 13, color: p.ink3 }}>Analyzing comparables…</div>}
          {error && <div style={{ fontSize: 12, color: T.brick }}>{error}</div>}
          {cma && !loading && (
            <>
              <div>
                <Over color={p.ink3}>Estimated value range</Over>
                <div style={{ ...boxStyle, marginTop: 6, fontFamily: F.mono, fontSize: 16, fontWeight: 600 }}>
                  {cma.estimated_value_low != null && cma.estimated_value_high != null
                    ? `${fmtCAD(cma.estimated_value_low)} – ${fmtCAD(cma.estimated_value_high)}`
                    : 'Not enough data to estimate'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <Over color={p.ink3}>$/sqft estimate</Over>
                  <div style={{ ...boxStyle, marginTop: 6 }}>{cma.price_per_sqft_estimate != null ? `$${cma.price_per_sqft_estimate}` : '—'}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <Over color={p.ink3}>Confidence</Over>
                  <div style={{ ...boxStyle, marginTop: 6, textTransform: 'capitalize' }}>{cma.confidence}</div>
                </div>
              </div>
              {cma.mls_sold_source && cma.mls_sold_source !== 'connected' && (
                <div style={{ ...boxStyle, color: p.ink3 }}>
                  {cma.mls_sold_source === 'not_connected'
                    ? 'Using your office’s closed deals only. Connect MLS in Connected tools to add board sales.'
                    : cma.mls_sold_source === 'sandbox'
                      ? 'Test feed data — treat this estimate as a test.'
                      : 'Board sales unavailable. Using your office’s closed deals only.'}
                </div>
              )}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Over color={p.ink3}>Analysis</Over>
                  {analysisEdited && <button style={linkBtn} onClick={() => { setAnalysis(cma.analysis || ''); setAnalysisEdited(false); }}>Undo my edits</button>}
                </div>
                <textarea
                  value={analysis}
                  onChange={e => { setAnalysis(e.target.value); setAnalysisEdited(true); }}
                  rows={4}
                  maxLength={4000}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                />
                <div style={{ fontSize: 11, color: p.ink3, marginTop: 4 }}>Edit freely. The range stays tied to the comps.</div>
              </div>
              <div>
                <Over color={p.ink3}>Sold comps used ({cma.sold_comps_used?.length || 0})</Over>
                {(cma.sold_comps_used || []).length === 0 ? (
                  <div style={{ ...boxStyle, marginTop: 6, color: p.ink3 }}>No sold comps in the last 6 months.</div>
                ) : (
                  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {cma.sold_comps_used.map(c => (
                      <div key={c.key || `${c.source}-${c.id}`} style={{ ...boxStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                          <span>{c.property_address}</span>
                          <span style={{ fontSize: 11, color: p.ink3 }}>{compMeta(c)}</span>
                        </span>
                        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                          <span style={{ fontFamily: F.mono }}>{fmtCAD(c.sale_price)}</span>
                          {c.key && <button style={linkBtn} onClick={() => toggleComp(c.key)} title="Leave this sale out and recalculate">Leave out</button>}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {(cma.excluded_comps || []).length > 0 && (
                <div>
                  <Over color={p.ink3}>Left out ({cma.excluded_comps.length})</Over>
                  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {cma.excluded_comps.map(c => (
                      <div key={c.key} style={{ ...boxStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, color: p.ink3 }}>
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                          <span style={{ textDecoration: 'line-through' }}>{c.property_address}</span>
                          <span style={{ fontSize: 11 }}>{compMeta(c)} · {fmtCAD(c.sale_price)}</span>
                        </span>
                        <button style={linkBtn} onClick={() => toggleComp(c.key)}>Put back</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Over color={p.ink3}>Active comps used ({cma.active_comps_used?.length || 0})</Over>
                {(cma.active_comps_used || []).length === 0 ? (
                  <div style={{ ...boxStyle, marginTop: 6, color: p.ink3 }}>No other active listings found in this office's records yet.</div>
                ) : (
                  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {cma.active_comps_used.map(c => (
                      <div key={c.id} style={{ ...boxStyle, display: 'flex', justifyContent: 'space-between' }}>
                        <span>{c.property_address}</span>
                        <span style={{ fontFamily: F.mono }}>{fmtCAD(c.list_price)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ borderTop: `1px solid ${p.hair}`, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Over color={p.ink3}>Seller report (PDF)</Over>
                <label style={{ fontSize: 12, color: p.ink3 }}>
                  Prepared for
                  <input value={preparedFor} onChange={e => setPreparedFor(e.target.value)} maxLength={120} placeholder="Seller's name" style={inputStyle}/>
                </label>
                <label style={{ fontSize: 12, color: p.ink3 }}>
                  Note for the seller
                  <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={2000} placeholder="Optional" style={{ ...inputStyle, resize: 'vertical' }}/>
                </label>
                <div style={{ fontSize: 11, color: p.ink3, lineHeight: 1.5 }}>
                  Includes every comp used and states it's not an appraisal.
                  {!linkedDeal && ' Link a deal to file it there.'}
                </div>
                {filed && (
                  <div style={{ ...boxStyle, color: p.ink2 }}>
                    Saved to the deal as “{filed.document?.name}”.{' '}
                    <Link to={`/transactions/${filed.transaction_id}`} style={{ color: T.brick }}>Open deal</Link>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <Btn variant="primary" dark={dark} onClick={downloadPdf} disabled={!!busy || loading}>{busy === 'pdf' ? 'Building PDF…' : 'Download PDF'}</Btn>
                  <Btn variant="ghost" dark={dark} onClick={fileToDeal} disabled={!!busy || loading || !linkedDeal} title={linkedDeal ? '' : 'Link this listing to a deal first'}>{busy === 'file' ? 'Saving…' : 'Save to deal'}</Btn>
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter style={{ marginTop: 24, gap: 10 }}>
          <Btn variant="ghost" dark={dark} onClick={onClose}>Close</Btn>
          <Btn variant="ghost" dark={dark} onClick={() => handleGenerate()} disabled={loading}>{loading ? 'Analyzing…' : 'Regenerate'}</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Document Drafter dialog ────────────────────────────────────────

const DRAFT_DOC_OPTIONS = [
  { id: 'seller_update', label: 'Seller status update' },
  { id: 'price_reduction_letter', label: 'Price reduction letter' },
  { id: 'showing_request', label: "Showing request to buyer's agent" },
  { id: 'listing_alert', label: 'New listing alert to buyer' },
];

function DraftDocumentDialog({ listing, dark, p, session, BASE, onClose }) {
  const [docType, setDocType] = useState('seller_update');
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preparedFor, setPreparedFor] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // 'pdf' | 'file'
  const [filed, setFiled] = useState(null);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };
  const linkedDeal = listing.transaction_id;

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setFiled(null);
    try {
      const res = await fetch(`${BASE}/api/mls/listings/${listing.id}/draft-document`, { method: 'POST', headers, body: JSON.stringify({ doc_type: docType }) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Draft generation failed');
      }
      const data = await res.json();
      setDraft(data);
      setSubject(data.subject || '');
      setBody(data.body || '');
    } catch (e) { setError(e.message || 'Failed to generate draft'); }
    finally { setGenerating(false); }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copied to clipboard');
  };

  const reportBody = () => JSON.stringify({
    doc_type: draft?.doc_type || docType,
    subject,
    body,
    prepared_for: preparedFor,
    note,
  });

  const downloadPdf = async () => {
    setBusy('pdf');
    try {
      const res = await fetch(`${BASE}/api/mls/listings/${listing.id}/draft-document/report.pdf`, { method: 'POST', headers, body: reportBody() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Couldn't build the PDF");
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const slug = (listing.property_address || 'listing').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'listing';
      const kind = (draft?.doc_type || docType || 'draft').replace(/_/g, '-');
      a.download = m ? m[1] : `draft-${kind}-${slug}-${new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    } catch (e) { toast.error(e.message || "Couldn't build the PDF"); }
    finally { setBusy(null); }
  };

  const fileToDeal = async () => {
    setBusy('file');
    try {
      const res = await fetch(`${BASE}/api/mls/listings/${listing.id}/draft-document/report/file`, { method: 'POST', headers, body: reportBody() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Couldn't file the draft");
      setFiled(data);
      toast.success("Draft saved to the deal's documents");
    } catch (e) { toast.error(e.message || "Couldn't file the draft"); }
    finally { setBusy(null); }
  };

  const boxStyle = { padding: '12px 14px', border: `1px solid ${p.hair}`, borderRadius: 2, fontSize: 13, color: p.ink2, lineHeight: 1.5, whiteSpace: 'pre-wrap' };
  const inputStyle = { marginTop: 6, width: '100%', padding: '9px 11px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' };
  const linkBtn = { background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: T.brick, padding: 0, fontFamily: F.display, flexShrink: 0 };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent style={{ maxWidth: 560, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)', maxHeight: '90vh', overflowY: 'auto' }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Draft Document</DialogTitle>
          <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
            {listing.property_address} · from the fields on file.
          </DialogDescription>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <Over color={p.ink3}>Document type</Over>
              <select value={docType} onChange={e => { setDocType(e.target.value); setDraft(null); setSubject(''); setBody(''); setFiled(null); }}
                style={{ marginTop: 8, width: '100%', padding: '9px 11px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                {DRAFT_DOC_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <Btn variant="primary" dark={dark} onClick={handleGenerate} disabled={generating || !!busy}>{generating ? 'Drafting…' : draft ? 'Regenerate' : 'Draft'}</Btn>
          </div>

          {error && <div style={{ fontSize: 12, color: T.brick }}>{error}</div>}

          {draft && (
            <>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <Over color={p.ink3}>Subject</Over>
                  <button onClick={() => handleCopyToClipboard(subject)} style={linkBtn}>Copy</button>
                </div>
                <textarea
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  rows={2}
                  maxLength={200}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5, marginTop: 0 }}
                />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <Over color={p.ink3}>Body</Over>
                  <button onClick={() => handleCopyToClipboard(body)} style={linkBtn}>Copy</button>
                </div>
                <textarea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  rows={10}
                  maxLength={8000}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5, marginTop: 0 }}
                />
              </div>
              <div style={{ borderTop: `1px solid ${p.hair}`, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Over color={p.ink3}>PDF for the client</Over>
                <label style={{ fontSize: 12, color: p.ink3 }}>
                  Prepared for
                  <input value={preparedFor} onChange={e => setPreparedFor(e.target.value)} maxLength={120} placeholder="Client's name" style={inputStyle}/>
                </label>
                <label style={{ fontSize: 12, color: p.ink3 }}>
                  Note for the client
                  <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={2000} placeholder="Optional" style={{ ...inputStyle, resize: 'vertical' }}/>
                </label>
                <div style={{ fontSize: 11, color: p.ink3, lineHeight: 1.5 }}>
                  Send it from your own mail.
                  {!linkedDeal && ' Link a deal to file the PDF there.'}
                </div>
                {filed && (
                  <div style={{ ...boxStyle, color: p.ink2, whiteSpace: 'normal' }}>
                    Saved to the deal as “{filed.document?.name}”.{' '}
                    <Link to={`/transactions/${filed.transaction_id}`} style={{ color: T.brick }}>Open deal</Link>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <Btn variant="primary" dark={dark} onClick={downloadPdf} disabled={!!busy || generating || !subject.trim() || !body.trim()}>{busy === 'pdf' ? 'Building PDF…' : 'Download PDF'}</Btn>
                  <Btn variant="ghost" dark={dark} onClick={fileToDeal} disabled={!!busy || generating || !linkedDeal || !subject.trim() || !body.trim()} title={linkedDeal ? '' : 'Link this listing to a deal first'}>{busy === 'file' ? 'Saving…' : 'Save to deal'}</Btn>
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter style={{ marginTop: 24, gap: 10 }}>
          <Btn variant="ghost" dark={dark} onClick={onClose}>Close</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main page ──────────────────────────────────────────────────────

export default function MLSPage() {
  const { resolvedTheme } = useTheme();
  const { session } = useAuth();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  const [listings, setListings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [showImport, setShowImport] = useState(false);
  const [showLink, setShowLink] = useState(null);
  const [launchOn, setLaunchOn] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPackage, setShowPackage] = useState(null);
  const [showCopyGen, setShowCopyGen] = useState(null);
  const [showCma, setShowCma] = useState(null);
  const [showDraft, setShowDraft] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [linkTx, setLinkTx] = useState('');
  const [pullQuery, setPullQuery] = useState('');
  const [pullResults, setPullResults] = useState([]);
  const [pullLoading, setPullLoading] = useState(false);
  const [pullError, setPullError] = useState('');
  const [pullSandbox, setPullSandbox] = useState(false);
  const [mlsConnected, setMlsConnected] = useState(null);
  const [mlsSandbox, setMlsSandbox] = useState(false);
  const [mlsInfo, setMlsInfo] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [subSummary, setSubSummary] = useState(null);
  const [focusId, setFocusId] = useState(null);
  const [focusChecks, setFocusChecks] = useState(null);
  const pullTimer = useRef(null);

  const BASE = process.env.REACT_APP_BACKEND_URL;
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };

  const loadSubmissions = () => {
    fetch(`${BASE}/api/mls/submissions/summary`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => setSubSummary(d && Array.isArray(d.queue) ? d : null))
      .catch(() => setSubSummary(null));
  };

  // Launch kit is hidden unless the office has it switched on. ?link=<id> opens "Link to a deal" (from the kit).
  useEffect(() => {
    fetch(`${BASE}/api/listing-launch/enabled`, { headers }).then(r => r.ok ? r.json() : null)
      .then(d => setLaunchOn(!!(d && d.enabled))).catch(() => {});
    const lid = searchParams.get('link');
    if (lid) { setShowLink(lid); setSearchParams({}, { replace: true }); }
  }, []); // eslint-disable-line

  const load = () => {
    fetch(`${BASE}/api/mls/listings`, { headers }).then(r => r.json()).then(d => { setListings(Array.isArray(d) ? d : []); setLoading(false); }).catch(() => setLoading(false));
    fetch(`${BASE}/api/transactions`, { headers }).then(r => r.json()).then(d => setTransactions(Array.isArray(d) ? d : [])).catch(() => {});
    loadSubmissions();
    fetch(`${BASE}/api/mls/connection`, { headers })
      .then(r => r.ok ? r.json() : { connected: false })
      .then(d => {
        setMlsConnected(d && d.connected === true);
        setMlsSandbox(d && d.sandbox === true);
        setMlsInfo(d || null);
      })
      .catch(() => { setMlsConnected(false); setMlsSandbox(false); });
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    if (pullTimer.current) clearTimeout(pullTimer.current);
    const q = pullQuery.trim();
    if (q.length < 3) {
      setPullResults([]);
      setPullError('');
      setPullSandbox(false);
      setPullLoading(false);
      return;
    }
    setPullLoading(true);
    pullTimer.current = setTimeout(async () => {
      try {
        const compact = q.replace(/\s+/g, '');
        const looksMls = /^[A-Za-z]{1,3}\d{5,}$/.test(compact);
        const param = looksMls
          ? `mls_number=${encodeURIComponent(compact)}`
          : `q=${encodeURIComponent(q)}`;
        const res = await fetch(`${BASE}/api/mls/lookup?${param}`, { headers });
        if (res.status === 503) {
          const err = await res.json().catch(() => ({}));
          setPullResults([]);
          setPullSandbox(false);
          setPullError(err.error === 'repliers_unconfigured'
            ? 'Connect MLS to pull, or enter it manually.'
            : 'Pull unavailable. Enter it manually.');
          return;
        }
        if (!res.ok) {
          setPullResults([]);
          setPullSandbox(false);
          setPullError('Could not reach your MLS feed. Enter the listing manually.');
          return;
        }
        const data = await res.json();
        const hits = Array.isArray(data.listings) ? data.listings : [];
        setPullResults(hits);
        setPullSandbox(data.sandbox === true);
        setPullError(hits.length ? '' : 'No matching listings.');
      } catch {
        setPullResults([]);
        setPullSandbox(false);
        setPullError('Could not reach your MLS feed. Enter the listing manually.');
      } finally {
        setPullLoading(false);
      }
    }, 300);
    return () => { if (pullTimer.current) clearTimeout(pullTimer.current); };
  }, [pullQuery]); // eslint-disable-line

  const applyPull = (listing) => {
    setForm(f => ({
      ...f,
      mls_number: listing.mls_number || f.mls_number,
      property_address: listing.property_address || f.property_address,
      city: listing.city || f.city,
      province: listing.province || f.province,
      postal_code: listing.postal_code || f.postal_code,
      bedrooms: listing.bedrooms != null && listing.bedrooms !== '' ? String(listing.bedrooms) : f.bedrooms,
      bathrooms: listing.bathrooms != null && listing.bathrooms !== '' ? String(listing.bathrooms) : f.bathrooms,
      sqft: listing.sqft != null && listing.sqft !== '' ? String(listing.sqft) : f.sqft,
      list_price: listing.list_price != null && listing.list_price !== '' ? String(listing.list_price) : f.list_price,
      status: listing.status || f.status,
      property_type: listing.property_type || f.property_type,
      list_date: listing.list_date || f.list_date,
    }));
    setPullResults([]);
    setPullQuery(listing.mls_number ? `MLS# ${listing.mls_number}` : (listing.property_address || pullQuery));
    toast.success(pullSandbox ? "Filled from sandbox pull — review and import" : "Filled from MLS — review and import");
  };

  const handleImport = async () => {
    if (!form.mls_number || !form.property_address) return toast.error("MLS number and address required");
    try {
      const res = await fetch(`${BASE}/api/mls/listings`, { method:'POST', headers, body:JSON.stringify(form) });
      if (!res.ok) throw new Error();
      const created = await res.json().catch(() => null);
      const match = transactions.find(t =>
        t.mls_number && form.mls_number &&
        String(t.mls_number).toLowerCase() === String(form.mls_number).toLowerCase()
      );
      if (match && created?.id) {
        await fetch(`${BASE}/api/mls/listings/${created.id}/link`, {
          method:'POST', headers, body:JSON.stringify({ transaction_id: match.id }),
        });
        toast.success("Listing imported and linked to the existing deal");
      } else {
        toast.success("Listing imported");
      }
      setShowImport(false);
      setForm(EMPTY_FORM);
      setPullQuery('');
      setPullResults([]);
      setPullError('');
      setPullSandbox(false);
      load();
    } catch { toast.error("Failed to import listing"); }
  };

  const handleLink = async () => {
    if (!linkTx) return toast.error("Please select a transaction");
    try {
      await fetch(`${BASE}/api/mls/listings/${showLink}/link`, { method:'POST', headers, body:JSON.stringify({ transaction_id:linkTx }) });
      toast.success("Linked to transaction");
      setShowLink(null);
      setLinkTx('');
      load();
    } catch { toast.error("Failed to link"); }
  };

  const TABS = ['All', 'Active', 'Sold', 'Expired', 'Withdrawn'];
  const filtered = listings.filter(l => {
    const tabMatch = activeTab === 'All' || l.status?.toLowerCase() === activeTab.toLowerCase();
    if (!search) return tabMatch;
    const q = search.toLowerCase();
    return tabMatch && (l.property_address?.toLowerCase().includes(q) || l.mls_number?.toLowerCase().includes(q) || l.city?.toLowerCase().includes(q));
  });

  const rowLink = { background:'none', border:'none', padding:0, cursor:'pointer', fontSize:13, color:p.ink2, fontFamily:F.display, textDecoration:'underline', textUnderlineOffset:3, textDecorationColor:p.hairS, whiteSpace:'nowrap' };
  const queue = subSummary?.queue || [];
  const queueById = Object.fromEntries(queue.map(q => [q.id, q]));
  const focus = focusItem(queue, focusId);
  const focusKey = focus ? `${focus.id}:${focus.updated_at || ''}` : '';
  useEffect(() => {
    if (!focus?.id) { setFocusChecks(null); return undefined; }
    let live = true;
    setFocusChecks(null);
    fetch(`${BASE}/api/mls/listings/${focus.id}/checks`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (live) setFocusChecks(d && Array.isArray(d.checks) ? d : null); })
      .catch(() => { if (live) setFocusChecks(null); });
    return () => { live = false; };
  }, [focusKey]); // eslint-disable-line
  const openPackage = (id, board) => {
    const l = listings.find(x => x.id === id);
    if (l) setShowPackage({ listing: l, board });
  };

  if (loading) return <Loading />;

  const totalActive = listings.filter(l => l.status === 'active').length;
  const totalSold = listings.filter(l => l.status === 'sold').length;
  const soldVolume = listings.filter(l => l.status === 'sold').reduce((s, l) => s + (l.list_price || 0), 0);

  return (
    <Page testid="mls-page" width={1100}>

      {showPackage && (
        <PackageDialog
          listing={showPackage.listing}
          initialBoard={showPackage.board}
          dark={dark} p={p}
          session={session}
          BASE={BASE}
          onClose={() => setShowPackage(null)}
          onChanged={load}
        />
      )}

      {showCopyGen && (
        <CopyGenDialog
          listing={showCopyGen}
          dark={dark} p={p}
          session={session}
          BASE={BASE}
          onClose={() => setShowCopyGen(null)}
          onSaved={load}
        />
      )}

      {showCma && (
        <CmaDialog
          listing={showCma}
          dark={dark} p={p}
          session={session}
          BASE={BASE}
          onClose={() => setShowCma(null)}
        />
      )}

      {showDraft && (
        <DraftDocumentDialog
          listing={showDraft}
          dark={dark} p={p}
          session={session}
          BASE={BASE}
          onClose={() => setShowDraft(null)}
        />
      )}

      <PageHeader title="MLS listings"
        subtitle={mlsConnected
          ? (mlsSandbox
            ? 'Test feed connected. Not your live board.'
            : `Connected to ${mlsInfo?.provider_label || 'your MLS feed'}.${mlsInfo?.last_listing_sync_at ? ` Synced ${new Date(mlsInfo.last_listing_sync_at).toLocaleString('en-CA', { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' })}.` : ''}`)
          : <>Connect your board feed to pull listings. <Link to="/integrations" style={{ color:p.ink, textDecoration:'underline', textUnderlineOffset:3, textDecorationColor:p.hairS }}>Connect MLS</Link></>}
        action={<span style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {mlsConnected === true && mlsInfo?.can_sync_listings && (
            <Btn variant="ghost" dark={dark} disabled={syncing} style={{ whiteSpace:'nowrap' }} onClick={async () => {
              setSyncing(true);
              try {
                const r = await fetch(`${BASE}/api/mls/sync`, { method: 'POST', headers });
                const b = await r.json().catch(() => ({}));
                if (!r.ok) { toast.error(typeof b.detail === 'string' ? b.detail : 'Could not sync listings from your feed.'); return; }
                toast.success(`Synced ${b.upserted} listing${b.upserted === 1 ? '' : 's'} from your feed${b.complete ? '' : '. More remain; sync again to continue'}.`);
                load();
              } catch { toast.error('Could not sync listings from your feed.'); }
              finally { setSyncing(false); }
            }}>{syncing ? 'Syncing…' : 'Sync'}</Btn>
          )}
          <Btn variant="primary" dark={dark} style={{ whiteSpace:'nowrap' }} onClick={() => {
            setPullQuery(''); setPullResults([]); setPullError(''); setPullSandbox(false); setShowImport(true);
          }}>Add listing</Btn>
        </span>} />

      <Stats items={[
        { label: 'Active', value: String(totalActive) },
        { label: 'Sold', value: String(totalSold) },
        { label: 'Sold volume', value: <Money value={soldVolume} /> },
      ]} />

      {subSummary && (() => {
        const k = subSummary.kpis || {};
        const c = subSummary.counts || {};
        return (
          <Section testid="mls-submission-desk" title="Board submissions"
            note={[`${c.draft || 0} draft`, `${c.ready || 0} validated`, `${c.submitted || 0} submitted`, `${k.live || c.live || 0} live`, k.needs_fix ? `${k.needs_fix} to fix` : null].filter(Boolean).join(' · ')}>
            {queue.length === 0 ? (
              <Empty action={<TextLink onClick={() => setShowImport(true)}>Add a listing</TextLink>}>Nothing in the queue.</Empty>
            ) : (
              <List testid="mls-queue">
                {queue.map(q => {
                  const open = focus?.id === q.id;
                  return (
                    <div key={q.id}>
                      <Row testid={`queue-${q.id}`} onClick={() => setFocusId(open ? null : q.id)}
                        title={q.address || 'Listing'}
                        meta={[q.board_label, q.mls_number ? { text: q.mls_number, figure: true } : null, figLabel(`${q.fields_ready}/${q.fields_total}`, 'fields')]}
                        next={q.missing_count ? [{ text: `Missing ${q.missing_count} field${q.missing_count === 1 ? '' : 's'}`, attention: true }] : null}
                        status={STATUS_LABEL[q.status]}
                        attention={STATUS_TONE[q.status] === 'bad'} />
                      {open && (
                        <div data-testid="mls-validation" style={{ padding:'4px 4px 18px', display:'flex', flexDirection:'column', gap:10 }}>
                          {(focus.missing || []).length === 0
                            ? <div style={{ fontSize:13, color:p.ink2 }}>All {focus.fields_total} required fields ready for {focus.board_label}.</div>
                            : <div style={{ fontSize:13, color:'var(--c-attn)' }}>Still needed: {focus.missing.join(', ')}</div>}
                          <MediaChecks checks={focusChecks} p={p} />
                          <div><button onClick={() => openPackage(focus.id, focus.board)} style={{ ...rowLink, color:p.ink }}>
                            {focus.status === 'live' ? `Open ${focus.board_label} package` : focus.missing_count ? `Fix and submit to ${focus.board_label}` : `Submit to ${focus.board_label}`}
                          </button></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </List>
            )}
          </Section>
        );
      })()}

      <Controls>
        <StatusTabs label="Status" options={TABS.map(t => ({ key: t, label: t }))} value={activeTab} onChange={setActiveTab} testid="mls-tabs" />
        <Toolbar><Search value={search} onChange={setSearch} placeholder="Search MLS number, address or city" testid="mls-search" /></Toolbar>
      </Controls>

      {filtered.length === 0 ? (
        <Empty action={search ? null : <TextLink onClick={() => setShowImport(true)}>Add a listing</TextLink>}>
          {search ? 'No listings match your search.' : activeTab === 'All' ? 'No listings yet.' : `No ${activeTab.toLowerCase()} listings.`}
        </Empty>
      ) : (
        <List testid="mls-listings">
          {filtered.map(listing => {
            const sub = queueById[listing.id];
            return (
              <Row key={listing.id} testid={`listing-${listing.id}`}
                title={listing.property_address}
                meta={[listing.mls_number ? { text: listing.mls_number, figure: true } : null,
                  [listing.city, listing.province].filter(Boolean).join(', '),
                  { text: <span style={{ fontFamily:F.mono, fontSize:11.5 }}><Money value={listing.list_price} /></span> },
                  listing.property_type,
                  listing.bedrooms ? `${listing.bedrooms} bd${listing.bathrooms ? `, ${listing.bathrooms} ba` : ''}` : null]}
                next={<div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  <Meta parts={[sub ? `${sub.board_label}: ${STATUS_LABEL[sub.status].toLowerCase()}` : null,
                    listing.transaction_id ? 'Linked to a deal' : null,
                    listing.list_date ? `Listed ${fmtDate(listing.list_date)}` : null]} />
                  <span style={{ display:'flex', gap:14, alignItems:'center', flexWrap:'wrap' }}>
                    {launchOn && <button data-testid="mls-launch-kit" onClick={() => navigate(`/mls/${listing.id}/launch`)} style={rowLink}>Launch kit</button>}
                    <button onClick={() => setShowPackage({ listing, board: sub?.board })} title="MLS submission package" style={rowLink}>Package</button>
                    <button onClick={() => setShowCopyGen(listing)} style={rowLink}>Listing copy</button>
                    <button onClick={() => setShowCma(listing)} style={rowLink}>CMA</button>
                    <button onClick={() => setShowDraft(listing)} style={rowLink}>Draft document</button>
                    {!listing.transaction_id && <button onClick={() => setShowLink(listing.id)} style={rowLink}>Link to a deal</button>}
                  </span>
                </div>}
                status={humanize(listing.status || 'active')}
                attention={MLS_STATUS_TONE[listing.status] === 'brick'} />
            );
          })}
        </List>
      )}

      {/* Import Listing Modal */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent style={{ maxWidth:640, borderRadius:2, background:p.bg, border:`1px solid ${p.hair}`, padding:'var(--bx-pad)', maxHeight:'90vh', overflowY:'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily:F.display, fontSize:18, fontWeight:500, color:p.ink }}>Add a listing</DialogTitle>
            <DialogDescription style={{ fontFamily:F.display, fontSize:13, color:p.ink3, marginTop:4 }}>
              Pull from your MLS feed or enter it by hand.
              {JSON.stringify(form) !== JSON.stringify(EMPTY_FORM) && (
                <> <button type="button" data-testid="mls-start-blank" onClick={() => { setForm(EMPTY_FORM); setPullQuery(''); setPullResults([]); setPullError(''); setPullSandbox(false); }}
                  style={{ background:'none', border:'none', padding:0, cursor:'pointer', fontFamily:F.display, fontSize:13, color:p.ink2, textDecoration:'underline', textUnderlineOffset:3, textDecorationColor:p.hairS }}>Start a blank listing</button></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div style={{ paddingTop:16, marginBottom:4 }}>
            <Over color={p.ink3}>Pull from MLS</Over>
            <div style={{ position:'relative', marginTop:8 }}>
              <I name="srch" size={14} color={p.ink3} style={{ position:'absolute', left:10, top:13, pointerEvents:'none' }}/>
              <input
                type="text"
                value={pullQuery}
                onChange={e => setPullQuery(e.target.value)}
                placeholder="MLS number or address"
                style={{ width:'100%', padding:'9px 11px 9px 32px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:13, fontFamily:F.display, color:p.ink, background:'transparent', outline:'none', boxSizing:'border-box' }}
              />
              {pullLoading && (
                <BricksLoader variant="cycle" size={14} color={T.brick} style={{ position: 'absolute', right: 10, top: 11 }} />
              )}
              {pullResults.length > 0 && (
                <div style={{ position:'absolute', left:0, right:0, top:'100%', zIndex:20, background:p.bg, border:`1px solid ${p.hair}`, borderTop:'none', borderRadius:'0 0 2px 2px', maxHeight:240, overflowY:'auto' }}>
                  {pullResults.map((hit, i) => (
                    <button key={hit.mls_number || i} type="button" onClick={() => applyPull(hit)}
                      style={{ display:'block', width:'100%', textAlign:'left', padding:'10px 12px', background:'none', border:'none', borderBottom:i===pullResults.length-1?'none':`1px solid ${p.hair}`, cursor:'pointer', fontFamily:F.display }}>
                      <div style={{ fontSize:13, color:p.ink, fontWeight:500 }}>{hit.property_address || '—'}{hit.city ? `, ${hit.city}` : ''}{hit.province ? ` ${hit.province}` : ''}</div>
                      <div style={{ fontSize:11, color:p.ink3, marginTop:2, fontFamily:F.mono }}>
                        {hit.mls_number ? `MLS# ${hit.mls_number}` : ''}
                        {hit.list_price != null ? ` · ${fmtCAD(hit.list_price)}` : ''}
                        {hit.bedrooms != null ? ` · ${hit.bedrooms}bd` : ''}
                        {hit.bathrooms != null ? ` ${hit.bathrooms}ba` : ''}
                        {hit.property_type ? ` · ${hit.property_type}` : ''}
                        {hit.status ? ` · ${hit.status}` : ''}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {pullError && <div style={{ fontSize:12, color:p.ink3, marginTop:8 }}>{pullError}</div>}
            {pullSandbox && <div style={{ fontSize:12, color:p.ink3, marginTop:8 }}>From a test feed, not your live board.</div>}
            <div style={{ fontSize:11, color:p.ink4, marginTop:8 }}>Pull fills the form. Every field stays editable.</div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:0, paddingTop:8 }}>
            {(() => {
              const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
              return (<>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:14, paddingBottom:16 }}>
                  <ImportField label="MLS number" p={p} value={form.mls_number} onChange={set('mls_number')} placeholder="e.g. X9876543"/>
                  <ImportField label="Status" p={p} value={form.status} onChange={set('status')} options={['active','sold','expired','withdrawn']}/>
                  <div style={{ gridColumn: 'span 2' }}>
                    <Over color={p.ink3}>Property address</Over>
                    <AddressAutocomplete
                      value={form.property_address}
                      onChange={v => setForm(f => ({ ...f, property_address: v }))}
                      onSelect={s => setForm(f => ({
                        ...f,
                        property_address: s.label || f.property_address,
                        city: s.city || f.city,
                        province: mapProvince(s.province) || f.province,
                        postal_code: s.postal_code || f.postal_code,
                      }))}
                      placeholder="e.g. 456 Oak Avenue"
                      p={p}
                      style={{ marginTop: 8, width: '100%', padding: '9px 11px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <ImportField label="City" p={p} value={form.city} onChange={set('city')} placeholder="Toronto"/>
                  <ImportField label="Province" p={p} value={form.province} onChange={set('province')} options={['ON','BC','AB','MB','SK','QC','NS','NB','NL','PE']}/>
                  <ImportField label="Postal code" p={p} value={form.postal_code} onChange={set('postal_code')} placeholder="M5V 2T6"/>
                  <ImportField label="List price (CAD)" p={p} value={form.list_price} onChange={set('list_price')} type="number" placeholder="750000"/>
                  <ImportField label="Property type" p={p} value={form.property_type} onChange={set('property_type')}
                    options={['Detached','Semi-Detached','Townhouse','Condo Apt','Condo Townhouse','Commercial/Retail','Land','Other']}/>
                </div>

                <ImportSection title="Location & legal" p={p}>
                  <ImportField label="Legal description" p={p} hint="Lot/Plan/Concession" value={form.legal_description} onChange={set('legal_description')} span/>
                  <ImportField label="Lot front" p={p} value={form.lot_front} onChange={set('lot_front')} type="number"/>
                  <ImportField label="Lot depth" p={p} value={form.lot_depth} onChange={set('lot_depth')} type="number"/>
                  <ImportField label="Lot size units" p={p} value={form.lot_size_code} onChange={set('lot_size_code')} options={['Feet','Metres','Acres']}/>
                  <ImportField label="Zoning" p={p} value={form.zoning} onChange={set('zoning')}/>
                </ImportSection>

                <ImportSection title="Amounts & dates" p={p}>
                  <ImportField label="Taxes" p={p} value={form.taxes} onChange={set('taxes')} type="number"/>
                  <ImportField label="Tax year" p={p} value={form.tax_year} onChange={set('tax_year')} type="number"/>
                  <ImportField label="List date" p={p} value={form.list_date} onChange={set('list_date')} type="date"/>
                  <ImportField label="Possession date" p={p} value={form.possession_date} onChange={set('possession_date')} type="date"/>
                  <ImportField label="Possession remarks" p={p} value={form.possession_remarks} onChange={set('possession_remarks')} span/>
                </ImportSection>

                <ImportSection title="Exterior & structure" p={p}>
                  <ImportField label="Style" p={p} hint="Bungalow / 2-Storey / etc." value={form.style} onChange={set('style')}/>
                  <ImportField label="Exterior finish" p={p} value={form.exterior_finish} onChange={set('exterior_finish')}/>
                  <ImportField label="Approx. Age" p={p} value={form.approx_age} onChange={set('approx_age')}/>
                  <ImportField label="Garage type" p={p} value={form.garage_type} onChange={set('garage_type')}/>
                  <ImportField label="Garage spaces" p={p} value={form.garage_spaces} onChange={set('garage_spaces')} type="number"/>
                  <ImportField label="Driveway parking spaces" p={p} value={form.drive_parking_spaces} onChange={set('drive_parking_spaces')} type="number"/>
                  <ImportField label="Total parking spaces" p={p} value={form.total_parking_spaces} onChange={set('total_parking_spaces')} type="number"/>
                  <ImportField label="Pool" p={p} value={form.pool} onChange={set('pool')}/>
                  <ImportField label="Waterfront" p={p} value={form.waterfront} onChange={set('waterfront')} options={['None','Direct','Indirect']}/>
                </ImportSection>

                <ImportSection title="Interior" p={p}>
                  <ImportField label="Bedrooms" p={p} value={form.bedrooms} onChange={set('bedrooms')} type="number"/>
                  <ImportField label="Bathrooms" p={p} value={form.bathrooms} onChange={set('bathrooms')} type="number"/>
                  <ImportField label="Kitchens" p={p} value={form.kitchens} onChange={set('kitchens')} type="number"/>
                  <ImportField label="Total rooms" p={p} value={form.rooms_total} onChange={set('rooms_total')} type="number"/>
                  <ImportField label="Approx. Square Footage" p={p} value={form.sqft} onChange={set('sqft')} type="number"/>
                  <ImportField label="Basement" p={p} value={form.basement_type} onChange={set('basement_type')}/>
                  <ImportField label="Heat type" p={p} value={form.heat_type} onChange={set('heat_type')}/>
                  <ImportField label="Air conditioning" p={p} value={form.air_conditioning} onChange={set('air_conditioning')}/>
                </ImportSection>

                <ImportSection title="Remarks" p={p}>
                  <ImportField label="Remarks for clients" p={p} hint="Public-facing, ~463 characters" value={form.remarks_client} onChange={set('remarks_client')} type="textarea" span/>
                  <ImportField label="Remarks for offices" p={p} hint="Internal-only, ~280 characters" value={form.remarks_brokerage} onChange={set('remarks_brokerage')} type="textarea" span/>
                </ImportSection>

                <ImportSection title="Listing office/agent" p={p}>
                  <ImportField label="Listing office" p={p} value={form.listing_brokerage} onChange={set('listing_brokerage')}/>
                  <ImportField label="Listing agent" p={p} value={form.listing_agent_name} onChange={set('listing_agent_name')}/>
                  <ImportField label="Agent phone" p={p} value={form.listing_agent_phone} onChange={set('listing_agent_phone')}/>
                  <ImportField label="Co-op commission" p={p} hint="e.g. 2.5% or a flat amount" value={form.co_op_commission} onChange={set('co_op_commission')}/>
                </ImportSection>
              </>);
            })()}
          </div>
          <DialogFooter style={{ marginTop:24, gap:10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowImport(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleImport}>Import Listing</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Link to a deal */}
      <Dialog open={!!showLink} onOpenChange={v => !v && setShowLink(null)}>
        <DialogContent style={{ maxWidth:380, borderRadius:2, background:p.bg, border:`1px solid ${p.hair}`, padding:'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily:F.display, fontSize:18, fontWeight:500, color:p.ink }}>Link to a deal</DialogTitle>
            <DialogDescription style={{ fontFamily:F.display, fontSize:13, color:p.ink3, marginTop:4 }}>Attach this listing to a deal.</DialogDescription>
          </DialogHeader>
          <div style={{ paddingTop:20 }}>
            <Over color={p.ink3}>Deal</Over>
            <select value={linkTx} aria-label="Deal" onChange={e => setLinkTx(e.target.value)}
              style={{ marginTop:8, width:'100%', minHeight:44, padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:p.bg, outline:'none' }}>
              <option value="">Choose a deal…</option>
              {transactions.map(t => <option key={t.id} value={t.id}>{t.property_address} — {t.deal_type}</option>)}
            </select>
          </div>
          <DialogFooter style={{ marginTop:24, gap:10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowLink(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleLink}>Link deal</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
